import React, {useEffect, useRef} from 'react';
import {View, StyleSheet, Animated, ViewStyle} from 'react-native';
import { COLORS } from '../theme/themeTokens';

interface SkeletonProps {
  width?: number | string;
  height?: number;
  borderRadius?: number;
  style?: ViewStyle;
}

// Basic skeleton block with shimmer animation
export const SkeletonBlock: React.FC<SkeletonProps> = ({
  width = '100%',
  height = 20,
  borderRadius = 4,
  style,
}) => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [shimmerAnim]);

  const opacity = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 0.7],
  });

  return (
    <Animated.View
      style={[
        styles.skeleton,
        {
          width,
          height,
          borderRadius,
          opacity,
        },
        style,
      ]}
    />
  );
};

// Skeleton for a balance card
export const SkeletonBalanceCard: React.FC = () => (
  <View style={styles.balanceCard}>
    <SkeletonBlock width={180} height={16} style={{marginBottom: 14}} />
    <View style={styles.balanceRow}>
      <SkeletonBlock width={120} height={28} />
      <SkeletonBlock width={50} height={16} />
    </View>
  </View>
);

// Skeleton for a report item
export const SkeletonReportItem: React.FC = () => (
  <View style={styles.reportItem}>
    <SkeletonBlock width={32} height={32} borderRadius={8} />
    <View style={styles.reportInfo}>
      <SkeletonBlock width={150} height={14} />
      <SkeletonBlock width={80} height={12} style={{marginTop: 7}} />
    </View>
    <SkeletonBlock width={24} height={24} borderRadius={4} />
  </View>
);

// Skeleton for a settings row
export const SkeletonSettingsRow: React.FC = () => (
  <View style={styles.settingsRow}>
    <SkeletonBlock width={24} height={24} borderRadius={4} />
    <View style={styles.settingsContent}>
      <SkeletonBlock width={140} height={14} />
      <SkeletonBlock width={180} height={12} style={{marginTop: 3}} />
    </View>
    <SkeletonBlock width={51} height={30} borderRadius={15} />
  </View>
);

// Skeleton for a message bubble
export const SkeletonMessageBubble: React.FC<{isOutgoing?: boolean}> = ({
  isOutgoing = false,
}) => (
  <View
    style={[
      styles.messageBubble,
      isOutgoing ? styles.outgoing : styles.incoming,
    ]}>
    <SkeletonBlock
      width={isOutgoing ? 250 : 200}
      height={60}
      borderRadius={12}
    />
    <SkeletonBlock
      width={40}
      height={12}
      style={{marginTop: 12, alignSelf: isOutgoing ? 'flex-end' : 'flex-start'}}
    />
  </View>
);

// Dashboard skeleton
export const DashboardSkeleton: React.FC = () => (
  <View style={styles.container}>
    <SkeletonBalanceCard />
    <SkeletonBalanceCard />
    <SkeletonBalanceCard />
    <View style={styles.historySection}>
      <SkeletonBlock width={80} height={24} style={{marginBottom: 24}} />
      <SkeletonReportItem />
      <View style={styles.divider} />
      <SkeletonReportItem />
      <View style={styles.divider} />
      <SkeletonReportItem />
    </View>
  </View>
);

// Reports skeleton
export const ReportsSkeleton: React.FC = () => (
  <View style={styles.container}>
    <SkeletonBlock width={100} height={28} style={{marginBottom: 24}} />
    <View style={styles.reportsList}>
      <SkeletonReportItem />
      <View style={styles.divider} />
      <SkeletonReportItem />
      <View style={styles.divider} />
      <SkeletonReportItem />
    </View>
  </View>
);

// Support chat skeleton
export const SupportSkeleton: React.FC = () => (
  <View style={styles.container}>
    <SkeletonMessageBubble isOutgoing={false} />
    <SkeletonMessageBubble isOutgoing={true} />
  </View>
);

// Profile skeleton
export const ProfileSkeleton: React.FC = () => (
  <View style={styles.container}>
    <SkeletonSettingsRow />
    <SkeletonSettingsRow />
    <SkeletonSettingsRow />
    <SkeletonSettingsRow />
  </View>
);

const styles = StyleSheet.create({
  skeleton: {
    backgroundColor: COLORS.softGrey,
  },
  container: {
    padding: 20,
  },
  balanceCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    padding: 12,
    marginBottom: 14,
    shadowColor: 'rgba(42, 63, 93, 1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.1,
    shadowRadius: 2.8,
    elevation: 3,
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  reportItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    gap: 12,
  },
  reportInfo: {
    flex: 1,
  },
  reportsList: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
  },
  settingsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 39,
    marginBottom: 20,
    gap: 16,
  },
  settingsContent: {
    flex: 1,
  },
  messageBubble: {
    marginBottom: 12,
  },
  incoming: {
    alignItems: 'flex-start',
  },
  outgoing: {
    alignItems: 'flex-end',
  },
  historySection: {
    marginTop: 40,
  },
  divider: {
    height: 1,
    backgroundColor: COLORS.softGrey,
    marginVertical: 8,
  },
});

